import React, { useEffect, useRef, useCallback, useState } from 'react';
import './contract-type-management.scss';
import DataGrid, {
  DataGridRef,
  Column, Toolbar, Item, LoadPanel, Sorting,
  HeaderFilter, FilterRow, Scrolling, SearchPanel,
  StateStoring,
  Button as ColumnButton,
} from 'devextreme-react/data-grid';
import DevExtremePopup from 'devextreme-react/popup';
import Form, { SimpleItem, GroupItem, ButtonItem } from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import notify from 'devextreme/ui/notify';
import { useAuth } from '../../contexts/auth';
import {
  ContractTypeDto,
  ContractTypeFormData,
  getContractTypes,
  createContractType,
  updateContractType,
  deleteContractType,
  uploadTemplate,
  downloadTemplate,
  deleteTemplate,
} from '../../api/contract-types';

const DEFAULT_COLORS = [
  '#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0',
  '#00bcd4', '#795548', '#607d8b', '#e91e63', '#3f51b5',
];

export const ContractTypeManagement = () => {
  const { user } = useAuth();
  const gridRef = useRef<DataGridRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState<ContractTypeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [editingOid, setEditingOid] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<ContractTypeFormData>({
    Name: '', Color: '#2196f3', SortOrder: 0, IsActive: true,
  });
  const [uploadingOid, setUploadingOid] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [editingTemplateName, setEditingTemplateName] = useState<string | null>(null);
  const popupFileInputRef = useRef<HTMLInputElement>(null);

  const loadData = useCallback(async() => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const items = await getContractTypes(user.token);
      setData(items);
    } catch {
      notify('Fout bij laden van contracttypes', 'error', 3000);
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onAddClick = useCallback(() => {
    setEditingOid(null);
    setFormData({ Name: '', Color: '#2196f3', SortOrder: data.length, IsActive: true });
    setPendingFile(null);
    setEditingTemplateName(null);
    setPopupVisible(true);
  }, [data.length]);

  const onEditClick = useCallback((e: any) => {
    const row = e.row?.data as ContractTypeDto;
    if (!row) return;
    setEditingOid(row.Oid);
    setFormData({
      Name: row.Name,
      Color: row.Color,
      SortOrder: row.SortOrder,
      IsActive: row.IsActive,
    });
    setPendingFile(null);
    setEditingTemplateName(row.PdfTemplateFileName || null);
    setPopupVisible(true);
  }, []);

  const onDeleteClick = useCallback(async(e: any) => {
    const row = e.row?.data as ContractTypeDto;
    if (!row || !user?.token) return;
    try {
      await deleteContractType(row.Oid, user.token);
      notify('Contracttype succesvol verwijderd', 'success', 2000);
      loadData();
    } catch {
      notify('Fout bij verwijderen', 'error', 3000);
    }
  }, [user?.token, loadData]);

  const onSave = useCallback(async() => {
    if (!formData.Name) {
      notify('Naam is verplicht', 'error', 2000);
      return;
    }
    if (!user?.token) return;
    setSaving(true);
    try {
      let oid = editingOid;
      if (editingOid) {
        await updateContractType(editingOid, formData, user.token);
      } else {
        const created = await createContractType(formData, user.token);
        oid = created.Oid;
      }
      if (pendingFile && oid) {
        await uploadTemplate(oid, pendingFile, user.token);
      }
      notify(editingOid ? 'Contracttype succesvol bijgewerkt' : 'Contracttype succesvol aangemaakt', 'success', 2000);
      setPendingFile(null);
      setPopupVisible(false);
      loadData();
    } catch {
      notify('Er is een fout opgetreden bij het opslaan', 'error', 3000);
    } finally {
      setSaving(false);
    }
  }, [editingOid, formData, pendingFile, user?.token, loadData]);

  const onUploadTemplateClick = useCallback((row: ContractTypeDto) => {
    setUploadingOid(row.Oid);
    fileInputRef.current?.click();
  }, []);

  const onFileSelected = useCallback(async(e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingOid || !user?.token) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      notify('Alleen PDF bestanden zijn toegestaan', 'error', 3000);
      e.target.value = '';
      return;
    }

    try {
      await uploadTemplate(uploadingOid, file, user.token);
      notify('PDF template succesvol geüpload', 'success', 2000);
      loadData();
    } catch {
      notify('Fout bij uploaden van PDF template', 'error', 3000);
    } finally {
      e.target.value = '';
      setUploadingOid(null);
    }
  }, [uploadingOid, user?.token, loadData]);

  const onDownloadTemplate = useCallback(async(row: ContractTypeDto) => {
    if (!user?.token) return;
    try {
      const blob = await downloadTemplate(row.Oid, user.token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = row.PdfTemplateFileName || 'template.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notify('Fout bij downloaden van PDF template', 'error', 3000);
    }
  }, [user?.token]);

  const onDeleteTemplate = useCallback(async(row: ContractTypeDto) => {
    if (!user?.token) return;
    try {
      await deleteTemplate(row.Oid, user.token);
      notify('PDF template succesvol verwijderd', 'success', 2000);
      loadData();
    } catch {
      notify('Fout bij verwijderen van PDF template', 'error', 3000);
    }
  }, [user?.token, loadData]);

  const onClearFilters = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.instance().clearFilter();
      gridRef.current.instance().option('searchPanel.text', '');
    }
  }, []);

  const colorCellRender = useCallback((cellData: any) => {
    const color = cellData.value || '#ccc';
    return (
      <div className='color-cell'>
        <span className='color-swatch' style={{ backgroundColor: color }} />
        {color}
      </div>
    );
  }, []);

  const templateCellRender = useCallback((cellData: any) => {
    const row = cellData.data as ContractTypeDto;
    if (!row.HasPdfTemplate) {
      return (
        <Button
          icon='upload'
          text='Upload'
          stylingMode='text'
          onClick={() => onUploadTemplateClick(row)}
        />
      );
    }
    return (
      <div className='template-cell'>
        <Button
          icon='download'
          hint='Download'
          stylingMode='text'
          onClick={() => onDownloadTemplate(row)}
        />
        <Button
          icon='trash'
          hint='Verwijder template'
          stylingMode='text'
          onClick={() => onDeleteTemplate(row)}
        />
        <span>{row.PdfTemplateFileName}</span>
      </div>
    );
  }, [onUploadTemplateClick, onDownloadTemplate, onDeleteTemplate]);

  const activeCellRender = useCallback((cellData: any) => {
    return cellData.value ? 'Ja' : 'Nee';
  }, []);

  return (
    <div className='view contract-type-management'>
      <div className='view-wrapper-contract-type-management'>
        <input
          ref={fileInputRef}
          type='file'
          accept='.pdf'
          style={{ display: 'none' }}
          onChange={onFileSelected}
        />

        <DataGrid
          className='grid'
          ref={gridRef}
          noDataText=''
          focusedRowEnabled
          height='100%'
          dataSource={data}
          keyExpr='Oid'
          allowColumnReordering
          showBorders={false}
          onRowDblClick={(e) => {
            if (!e.data) return;
            setEditingOid(e.data.Oid);
            setFormData({
              Name: e.data.Name,
              Color: e.data.Color,
              SortOrder: e.data.SortOrder,
              IsActive: e.data.IsActive,
            });
            setPendingFile(null);
            setEditingTemplateName(e.data.PdfTemplateFileName || null);
            setPopupVisible(true);
          }}
        >
          <LoadPanel enabled={loading} showPane={false} />
          <SearchPanel width={240} visible placeholder='Zoek contracttype...' />
          <HeaderFilter visible allowSearch />
          <FilterRow visible />
          <Sorting mode='multiple' />
          <Scrolling mode='virtual' />
          <StateStoring
            enabled
            savingTimeout={500}
            type='localStorage'
            storageKey={`contractTypeMgmtState_${user?.tenantName || 'all'}`}
          />

          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>Contracttypes Beheer</div>
            </Item>
            <Item location='after' locateInMenu='auto'>
              <Button
                icon='plus'
                text='Contracttype'
                type='default'
                stylingMode='contained'
                onClick={onAddClick}
              />
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button
                icon='refresh'
                text='Vernieuwen'
                stylingMode='text'
                onClick={loadData}
              />
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button
                icon='clearformat'
                text='Filters wissen'
                stylingMode='text'
                onClick={onClearFilters}
              />
            </Item>
            <Item name='searchPanel' locateInMenu='auto' />
          </Toolbar>

          <Column dataField='Name' caption='Naam' sortOrder='asc' />
          <Column dataField='Color' caption='Kleur' width={160} cellRender={colorCellRender} />
          <Column dataField='SortOrder' caption='Volgorde' width={100} />
          <Column dataField='IsActive' caption='Actief' width={80} cellRender={activeCellRender} />
          <Column caption='PDF Template' width={280} cellRender={templateCellRender} allowFiltering={false} allowSorting={false} />
          <Column type='buttons' width={80}>
            <ColumnButton icon='edit' hint='Bewerken' onClick={onEditClick} />
            <ColumnButton icon='trash' hint='Verwijderen' onClick={onDeleteClick} />
          </Column>
        </DataGrid>

        <DevExtremePopup
          visible={popupVisible}
          onHiding={() => setPopupVisible(false)}
          title={editingOid ? 'Contracttype bewerken' : 'Nieuw contracttype'}
          showTitle
          width={520}
          height='auto'
          maxHeight='80vh'
          dragEnabled={false}
          showCloseButton
        >
          <Form
            formData={formData}
            onFieldDataChanged={(e) =>
              setFormData((prev) => ({ ...prev, [e.dataField as string]: e.value }))
            }
            colCount={1}
          >
            <GroupItem caption='Contracttype gegevens' colCount={1}>
              <SimpleItem dataField='Name' label={{ text: 'Naam' }} isRequired />
              <SimpleItem
                dataField='Color'
                label={{ text: 'Kleur' }}
                editorType='dxSelectBox'
                editorOptions={{
                  items: DEFAULT_COLORS,
                  acceptCustomValue: true,
                  itemTemplate: (itemData: string) => {
                    const el = document.createElement('div');
                    el.style.display = 'flex';
                    el.style.alignItems = 'center';
                    el.style.gap = '8px';
                    const swatch = document.createElement('span');
                    swatch.style.width = '20px';
                    swatch.style.height = '20px';
                    swatch.style.borderRadius = '4px';
                    swatch.style.backgroundColor = itemData;
                    swatch.style.border = '1px solid rgba(0,0,0,0.15)';
                    const label = document.createTextNode(itemData);
                    el.appendChild(swatch);
                    el.appendChild(label);
                    return el;
                  },
                }}
              />
              <SimpleItem dataField='SortOrder' label={{ text: 'Volgorde' }} editorType='dxNumberBox' />
              <SimpleItem dataField='IsActive' label={{ text: 'Actief' }} editorType='dxCheckBox' />
            </GroupItem>
            <GroupItem caption='PDF Template' colCount={1}>
              <SimpleItem render={() => (
                <div className='template-upload-section'>
                  <input
                    ref={popupFileInputRef}
                    type='file'
                    accept='.pdf'
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f && f.name.toLowerCase().endsWith('.pdf')) {
                        setPendingFile(f);
                        setEditingTemplateName(f.name);
                      } else if (f) {
                        notify('Alleen PDF bestanden zijn toegestaan', 'error', 3000);
                      }
                      e.target.value = '';
                    }}
                  />
                  {editingTemplateName || pendingFile ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className='dx-icon-pdffile' style={{ fontSize: '20px' }} />
                      <span>{pendingFile ? pendingFile.name : editingTemplateName}</span>
                      <Button
                        icon='trash'
                        hint='Verwijder template'
                        stylingMode='text'
                        onClick={() => {
                          setPendingFile(null);
                          setEditingTemplateName(null);
                          if (editingOid && user?.token) {
                            deleteTemplate(editingOid, user.token).then(() => loadData());
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <Button
                      icon='upload'
                      text='PDF uploaden'
                      stylingMode='outlined'
                      onClick={() => popupFileInputRef.current?.click()}
                    />
                  )}
                </div>
              )} />
            </GroupItem>
            <GroupItem colCount={2} cssClass='form-buttons'>
              <ButtonItem
                buttonOptions={{
                  text: 'Annuleren',
                  type: 'normal',
                  stylingMode: 'outlined',
                  onClick: () => setPopupVisible(false),
                }}
              />
              <ButtonItem
                buttonOptions={{
                  text: editingOid ? 'Opslaan' : 'Aanmaken',
                  type: 'default',
                  stylingMode: 'contained',
                  disabled: saving,
                  onClick: onSave,
                }}
              />
            </GroupItem>
          </Form>
        </DevExtremePopup>
      </div>
    </div>
  );
};
